/**
 * GIESTO — product card gallery.
 *
 * Drives the previous/next arrows on `snippets/giesto-store-card.liquid` and
 * keeps their disabled state in step with the scroll position.
 *
 * This lives in a shared asset rather than inside a section's custom element
 * because more than one section renders the card. It used to sit in
 * `<giesto-store-collection>`, which meant the arrows were dead on the sale
 * page: that page has no such element, so nothing was listening and clicking an
 * arrow did nothing at all — no error, just a control that ignored you.
 *
 * Delegated from the document, so cards added later — an infinite-scroll batch,
 * a cart-drawer recommendation, a section re-render — are covered without any
 * re-binding.
 */
(() => {
  const GALLERY = '[data-gac-gallery]';
  const PREV = '[data-gac-gallery-prev]';
  const NEXT = '[data-gac-gallery-next]';

  // The snippet is rendered on several page types, and each section that uses
  // it loads this file, so a second execution is possible.
  if (window.__giestoStoreCardGallery) return;
  window.__giestoStoreCardGallery = true;

  /** @param {Element} gallery */
  const syncArrows = (gallery) => {
    const card = gallery.closest('.gac-card');
    if (!card) return;

    // A fractional scrollLeft is normal mid-snap; a pixel of tolerance keeps the
    // ends from reading as "not quite there".
    const atStart = gallery.scrollLeft <= 1;
    const atEnd = gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 1;

    card.querySelector(PREV)?.toggleAttribute('disabled', atStart);
    card.querySelector(NEXT)?.toggleAttribute('disabled', atEnd);
  };

  window.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const moreColours = target.closest('.gac-card__colour-groups .csga-more');
    if (!moreColours) return;

    // The +N control is Swatch Sync's, not the reference's — the reference has
    // no such chip on a card at all. By request it behaves like the rest of the
    // card and opens the product, where every colour is listed, rather than
    // expanding the remaining swatches in place.
    // Capture phase is intentional: it runs before the app's own listener,
    // which would otherwise expand the row.
    event.preventDefault();
    event.stopImmediatePropagation();

    const href = moreColours.closest('.gac-card')?.querySelector('a[href*="/products/"]')?.href;
    if (href) window.location.assign(href);
  }, { capture: true });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prev = target.closest(PREV);
    const next = target.closest(NEXT);
    if (!prev && !next) return;

    // The arrows sit inside the card's product link.
    event.preventDefault();

    const gallery = target.closest('.gac-card')?.querySelector(GALLERY);
    if (!(gallery instanceof HTMLElement)) return;

    const amount = next ? gallery.clientWidth : -gallery.clientWidth;
    const before = gallery.scrollLeft;
    gallery.scrollBy({ left: amount, behavior: 'smooth' });

    // A smooth programmatic scroll can be dropped outright on a mandatory-snap
    // scroller — measured on this gallery: scrollLeft stayed at 0 for both
    // scrollBy and scrollTo with `behavior: 'smooth'`, with and without the snap
    // temporarily disabled, while the same call at the default behaviour moved
    // it the full width. Rather than leave the arrow looking dead when that
    // happens, check a beat later and jump. Where the animation does run this
    // sees the scroller already moving and does nothing.
    setTimeout(() => {
      if (Math.abs(gallery.scrollLeft - before) < 1) gallery.scrollBy({ left: amount });
    }, 250);
  });

  // Scroll does not bubble, but a capture-phase listener on the document still
  // sees it, so one listener covers every gallery on the page.
  document.addEventListener(
    'scroll',
    (event) => {
      const gallery = event.target;
      if (gallery instanceof HTMLElement && gallery.matches(GALLERY)) syncArrows(gallery);
    },
    { capture: true, passive: true }
  );
})();
